import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { addEpisodeFavorite, deleteProduct, getEpisodeById, removeEpisodeFavorite } from '@/api/api'
import type { Episode, Tag } from '@/api/type'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { confirmDialog } from '@/lib/confirm-dialog'
import { getOfflineEpisode, type OfflineEpisode } from '@/lib/db'
import { AUTOPLAY_TRACK_PARAM, MUSIC_DOMAIN } from '@/pages/music/constants'
import { ReaderSelector } from '@/pages/reader'
import { ReaderLoading } from '@/pages/reader/shared/reader-loading'
import { PATHS } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'
import { useMediaStore } from '@/store/useMediaStore'
import { NON_READABLE_MIME_TYPES } from '@/types/ebook-conversion'

import { InfoContainer } from './components/info-container'

/**
 * OfflineEpisodeから正確なmimeTypeを推定する
 * mimeTypeやBlob.typeが application/octet-stream の場合、Blobの先頭バイトから判定
 */
const detectMimeType = async (ep: OfflineEpisode): Promise<string> => {
  // Blob.typeが有効ならそちらを採用
  if (ep.blob?.type && ep.blob.type !== 'application/octet-stream') {
    return ep.blob.type
  }
  // 保存済みmimeTypeが有効ならそちらを採用
  if (ep.mimeType && ep.mimeType !== 'application/octet-stream') {
    return ep.mimeType
  }
  // マジックバイトから判定
  if (ep.blob && ep.blob.size >= 12) {
    const header = new Uint8Array(await ep.blob.slice(0, 12).arrayBuffer())
    // MP4: ftyp box
    if (header[4] === 0x66 && header[5] === 0x74 && header[6] === 0x79 && header[7] === 0x70) {
      return 'video/mp4'
    }
    // PDF: %PDF
    if (header[0] === 0x25 && header[1] === 0x50 && header[2] === 0x44 && header[3] === 0x46) {
      return 'application/pdf'
    }
    // ZIP/EPUB/CBZ: PK signature
    if (header[0] === 0x50 && header[1] === 0x4b) {
      return 'application/zip'
    }
  }
  return ep.mimeType
}

/** OfflineEpisodeからEpisodeオブジェクトを構築する */
const buildEpisodeFromOffline = (offlineEp: OfflineEpisode, mimeType: string): Episode => ({
  episode_id: offlineEp.episodeId,
  domain_episode_id: String(offlineEp.episodeId),
  mime_type: mimeType,
  file_name: '',
  custom_name: null,
  file_size: offlineEp.fileSize ?? offlineEp.blob?.size ?? null,
  num_pages: 0,
  upload_date: null,
  created_at: '',
  description: null,
  index: 0,
  is_favorite: false,
  product: {
    product_id: offlineEp.productId,
    domain: offlineEp.domain,
    domain_product_id: String(offlineEp.productId),
    title: offlineEp.title,
    thumbnail: null,
    description: null,
    tags: [],
    favorite_episode_count: 0,
    first_episode_id: null,
  },
})

type ComicReaderProps = {
  /** オーバーレイモード: 親から episode_id を渡す */
  overlayEpisodeId?: string
  /** オーバーレイモード: 前後の話へ移動したときのコールバック */
  onOverlayEpisodeChange?: (episodeId: string) => void
  /** オーバーレイモード: 閉じるコールバック */
  onOverlayClose?: () => void
  /** エピソード取得エラー時のコールバック */
  onError?: (message: string) => void
  /** 一覧が持っているエピソード情報。API の応答を待たずにリーダーを開くために使う */
  initialEpisode?: Episode | null
}

export const ComicReader = ({
  overlayEpisodeId,
  onOverlayEpisodeChange,
  onOverlayClose,
  onError,
  initialEpisode,
}: ComicReaderProps) => {
  // オーバーレイモードではpropsから、通常モードではURLパラメータからepisode_idを取得
  const { episode_id: routeEpisodeId } = useParams<'episode_id'>()
  const isOverlay = !!overlayEpisodeId

  // オーバーレイモードで前後遷移するための内部state
  const [internalEpisodeId, setInternalEpisodeId] = useState<string | undefined>(overlayEpisodeId)
  // overlayEpisodeIdが変わったら内部stateも更新
  useEffect(() => {
    if (overlayEpisodeId) setInternalEpisodeId(overlayEpisodeId)
  }, [overlayEpisodeId])

  const episode_id = isOverlay ? internalEpisodeId : routeEpisodeId

  // オーバーレイでは URL の ep パラメータが復帰の手がかりになるため、親にも移動先を伝える
  const changeOverlayEpisode = useCallback(
    (nextId: number) => {
      const id = nextId.toString()
      setInternalEpisodeId(id)
      onOverlayEpisodeChange?.(id)
    },
    [onOverlayEpisodeChange],
  )

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isOnline } = useOnlineStatus()
  const resetEpisode = useMediaStore((state) => state.resetEpisode)
  const startLoading = useAppStore((state) => state.startLoading)
  const stopLoading = useAppStore((state) => state.stopLoading)
  const stopLoadingRef = useRef(stopLoading)
  const onErrorRef = useRef(onError)
  useEffect(() => {
    stopLoadingRef.current = stopLoading
    onErrorRef.current = onError
  }, [stopLoading, onError])
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [episode, setEpisode] = useState<Episode | null>(null)
  const [isFavorite, setIsFavorite] = useState(false)

  useEffect(() => {
    resetEpisode()
    // 一覧の情報で先にリーダーを出し、前後話などは API の応答で埋める
    const seed = initialEpisode && String(initialEpisode.episode_id) === episode_id ? initialEpisode : null
    setEpisode(seed)
    if (seed) setIsFavorite(seed.is_favorite)
    // オーバーレイモードではグローバルloading状態を変更しない（親がアンマウントされるため）
    if (!isOverlay) startLoading()
  }, [resetEpisode, startLoading, episode_id, isOverlay, initialEpisode])

  // episode_id でエピソードを取得（前後エピソード情報含む）
  useEffect(() => {
    if (!episode_id) return

    const fetchEpisode = async () => {
      // オフライン時はAPI呼び出しをスキップし、IndexedDBから直接取得
      if (!navigator.onLine) {
        const offlineEp = await getOfflineEpisode(Number(episode_id))
        if (offlineEp) {
          const resolvedMimeType = await detectMimeType(offlineEp)
          setEpisode(buildEpisodeFromOffline(offlineEp, resolvedMimeType))
        } else {
          toast.error('このエピソードはダウンロードされていません')
        }
        if (!isOverlay) stopLoadingRef.current()
        return
      }

      try {
        const episodeData = await getEpisodeById(Number(episode_id), { includeAdjacent: true })
        setEpisode(episodeData)
        setIsFavorite(episodeData.is_favorite)
        if (!isOverlay) stopLoadingRef.current()
      } catch (error) {
        console.error('Error fetching episode:', error)

        // ネットワークエラー等: IndexedDBの保存済みデータでフォールバック
        const offlineEp = await getOfflineEpisode(Number(episode_id))
        if (offlineEp) {
          const resolvedMimeType = await detectMimeType(offlineEp)
          setEpisode(buildEpisodeFromOffline(offlineEp, resolvedMimeType))
          if (!isOverlay) stopLoadingRef.current()
          return
        }

        if (!isOverlay) stopLoadingRef.current()
        if (isOverlay && onErrorRef.current) {
          onErrorRef.current('エピソードの取得に失敗しました')
        }
      }
    }
    fetchEpisode()
  }, [episode_id, isOverlay])

  // 音楽（apple_music）は /music 配下の専用プレイヤーが担当するため、直リンクはそちらへ送る
  const isMusicEpisode = episode?.product.domain === MUSIC_DOMAIN
  useEffect(() => {
    if (!episode || !isMusicEpisode) return
    const albumPath = PATHS.MUSIC.ALBUM_DETAIL.replace(':product_id', String(episode.product.product_id))
    navigate(`${albumPath}?${AUTOPLAY_TRACK_PARAM}=${episode.episode_id}`, { replace: true })
  }, [episode, isMusicEpisode, navigate])

  // 前後のエピソード情報を取得（APIレスポンスから）
  const { prevEpisode, nextEpisode } = useMemo(() => {
    if (!episode) {
      return { prevEpisode: null, nextEpisode: null }
    }
    return {
      prevEpisode: episode.prev_episode ?? null,
      nextEpisode: episode.next_episode ?? null,
    }
  }, [episode])

  const images = useMemo(() => {
    if (!episode) return []
    return Array.from({ length: episode.num_pages ?? 0 }).map(
      (_, i) => `/api/e/${episode_id}/${i.toString().padStart(4, '0')}.webp`,
    )
  }, [episode, episode_id])

  const handleClose = useCallback(() => {
    // オーバーレイモード: 単純に閉じる（ProductContainerが裏にある）
    if (isOverlay && onOverlayClose) {
      onOverlayClose()
      return
    }

    // オフライン時はストレージ管理のオフラインタブに戻る
    if (!isOnline) {
      navigate('/storage-management?tab=offline')
      return
    }

    if (!episode?.product?.product_id) {
      navigate(-1)
      return
    }
    // AudioBookはエピソードシートを使わないため、シートを開かずに戻る
    // Singleプロダクト（前後エピソードなし＆ブラウザで読めるフォーマット）もドロワー不要
    const isSingleReadable = !prevEpisode && !nextEpisode && !NON_READABLE_MIME_TYPES.has(episode.mime_type)
    if (episode.product.domain === 'audiobook' || isSingleReadable) {
      navigate(`/p?domain=${episode.product.domain}`)
    } else {
      // 戻る際に、エピソードシートを開いた状態で戻る
      navigate(`/p?domain=${episode.product.domain}&openEpisodes=${episode.product.product_id}`)
    }
  }, [navigate, episode, prevEpisode, nextEpisode, isOverlay, onOverlayClose, isOnline])

  // お気に入りトグル
  const handleFavoriteToggle = useCallback(async () => {
    if (!episode) return
    try {
      if (isFavorite) {
        await removeEpisodeFavorite(episode.episode_id)
        toast.success('お気に入りから削除しました')
      } else {
        await addEpisodeFavorite(episode.episode_id)
        toast.success('お気に入りに追加しました')
      }
      setIsFavorite(!isFavorite)
    } catch (error) {
      console.error('Error toggling favorite:', error)
      toast.error('お気に入りの更新に失敗しました')
    }
  }, [episode, isFavorite])

  // 作品削除
  const handleDelete = useCallback(async () => {
    if (!episode) return
    if (!(await confirmDialog({ title: `「${episode.product.title}」を削除しますか？`, destructive: true }))) return
    try {
      await deleteProduct(episode.product.product_id)
      toast.success('削除しました')
      navigate(-1)
    } catch (error) {
      console.error('Error deleting product:', error)
      toast.error('削除に失敗しました')
    }
  }, [episode, navigate])

  // タグ更新ハンドラー
  const handleTagsUpdated = useCallback(
    (newTags: Tag[]) => {
      if (!episode) return
      setEpisode({
        ...episode,
        product: {
          ...episode.product,
          tags: newTags,
        },
      })
    },
    [episode],
  )

  // 前のエピソードに移動（episode_id を使用）
  const handlePrev = useCallback(() => {
    resetEpisode()
    if (!prevEpisode) return
    if (isOverlay) {
      changeOverlayEpisode(prevEpisode.episode_id)
    } else {
      const queryString = searchParams.toString()
      const url = `/p/${prevEpisode.episode_id}`
      navigate(queryString ? `${url}?${queryString}` : url)
    }
  }, [resetEpisode, prevEpisode, searchParams, navigate, isOverlay, changeOverlayEpisode])

  // 次のエピソードに移動（episode_id を使用）
  const handleNext = useCallback(() => {
    resetEpisode()
    if (!nextEpisode) return
    if (isOverlay) {
      changeOverlayEpisode(nextEpisode.episode_id)
    } else {
      const queryString = searchParams.toString()
      const url = `/p/${nextEpisode.episode_id}`
      navigate(queryString ? `${url}?${queryString}` : url)
    }
  }, [resetEpisode, nextEpisode, searchParams, navigate, isOverlay, changeOverlayEpisode])

  if (!episode) {
    // 通常モードはグローバルの loading 表示が担当する
    return isOverlay ? <ReaderLoading /> : null
  }

  // 音楽画面へリダイレクト中は何も描画しない（未対応フォーマット表示が一瞬出るのを防ぐ）
  if (isMusicEpisode) {
    return null
  }

  const baseUrl = `/e/${episode_id}`

  return (
    <div className="h-screen" key={episode.product.domain_product_id}>
      <ReaderSelector
        mimeType={episode.mime_type || ''}
        baseUrl={baseUrl}
        fileName={episode.file_name || ''}
        title={episode.product.title ?? ''}
        images={images}
        onClose={handleClose}
        onInfoClick={() => setIsDrawerOpen(true)}
        onNext={nextEpisode ? handleNext : undefined}
        onPrev={prevEpisode ? handlePrev : undefined}
        nextEpisodeId={nextEpisode?.episode_id?.toString()}
        nextEpisodeTitle={nextEpisode?.domain_episode_id}
        numPages={episode.num_pages ?? 0}
        domain={episode.product.domain}
        productId={episode.product.domain_product_id}
        episodeId={episode.episode_id?.toString()}
        episodeIndex={episode.index}
        productIdNum={episode.product.product_id}
        isFavorite={isFavorite}
        onFavoriteToggle={handleFavoriteToggle}
        onDelete={handleDelete}
      />

      <InfoContainer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false)
        }}
        title={episode.product.title ?? ''}
        description={episode.description ?? ''}
        tags={episode.product.tags ?? []}
        numPages={episode.num_pages ?? 0}
        mimeType={episode.mime_type}
        fileSize={episode.file_size}
        uploadDate={episode.upload_date ?? ''}
        domain={episode.product.domain}
        productId={episode.product.product_id}
        episodeId={episode.episode_id}
        onTagsUpdated={handleTagsUpdated}
      />
    </div>
  )
}
